/**
 * Automatismes (migration 0323) — séries de 10 questions flash sur le
 * programme en cours (packs déjà commencés) et les années antérieures du
 * cycle, comme la partie 1 « Automatismes » du DNB et de l'épreuve anticipée
 * de mathématiques de première. XP + HORA (3 séries récompensées par jour),
 * pas de rapport. Client des RPC + helpers PURS (testés).
 */

import { publishProgress } from '@/lib/progress-sync';
import { useQuery } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import type { DiagnosticChoice } from '@/lib/skill-diagnostic';

const db = supabase as unknown as SupabaseClient;

/** Miroir de `automatisme_levels` (SQL) : son année + les années antérieures du cycle. */
export const AUTOMATISME_LEVELS: Record<string, readonly string[]> = {
  '6e': ['6e'],
  '5e': ['6e', '5e'],
  '4e': ['5e', '4e'],
  '3e': ['5e', '4e', '3e'],
  '2nde': ['3e', '2nde'],
  '1re': ['2nde', '1re'],
  tle: ['2nde', '1re', 'tle'],
  mpsi: ['tle', 'mpsi'],
  ptsi: ['tle', 'ptsi'],
  mp: ['mpsi', 'mp'],
  pt: ['ptsi', 'pt'],
};

export const AUTOMATISMES_DAILY_REWARDS = 3;

export interface AutomatismeSession {
  id: string;
  status: 'in_progress' | 'completed' | 'abandoned';
  level: string;
  question_count: number;
  answered_count: number;
  started_at: string;
  completed_at: string | null;
  total: number | null;
  correct: number | null;
  score_pct: number | null;
  xp_awarded: number;
  hora_awarded: number;
  rewarded: boolean;
}

/** Accès (migration 0339) : 1 série gratuite par mois, illimité avec abonnement / pass / programme. */
export interface AutomatismesAccess {
  unlimited: boolean;
  reason: 'admin' | 'subscription' | 'pass' | 'programme' | null;
  free_per_month: number;
  /** Séries gratuites restantes ce mois-ci (null si illimité). */
  free_left: number | null;
  /** 1er jour du mois prochain (AAAA-MM-JJ). */
  next_free_at: string;
}

export interface AutomatismesState {
  access?: AutomatismesAccess;
  available: boolean;
  reason?: string;
  levels?: string[];
  question_count?: number;
  daily_reward_limit?: number;
  rewarded_today?: number;
  in_progress?: AutomatismeSession | null;
  last?: AutomatismeSession | null;
  sessions_count?: number;
  best_pct?: number | null;
}

export interface AutomatismeQuestion {
  step_id: string;
  card_id: string | null;
  card_title: string | null;
  prompt_md: string;
  /** Visuels de l'énoncé de contexte + de la question (migration 0342). */
  visuals?: unknown[];
  explanation_md: string | null;
  choices: DiagnosticChoice[];
}

export interface AutomatismesPlay {
  session: AutomatismeSession;
  questions: AutomatismeQuestion[];
  answered: { question_id: string; was_correct: boolean }[];
}

export interface AutomatismeCardResult {
  card_id: string;
  correct: number;
  total: number;
  hora_gain: number;
  title: string | null;
  levels: string[];
  color_theme: string | null;
  creature: unknown;
}

export interface AutomatismesResult {
  session: AutomatismeSession;
  cards: AutomatismeCardResult[];
}

// ── Helpers purs ────────────────────────────────────────────────────────────

/** Rappel de l'épreuve officielle correspondante (null si aucune). */
export function automatismesExamHint(level: string | null | undefined): string | null {
  if (level === '3e') {
    return 'Au brevet, la partie « Automatismes » vaut 6 points sur 20 : 20 minutes, sans calculatrice, sur tout le cycle 4 (5e, 4e, 3e).';
  }
  if (level === '1re') {
    return 'À l’épreuve anticipée de maths de première, la partie « Automatismes » vaut 6 points sur 20, sans calculatrice, sur la seconde et la première.';
  }
  return null;
}

/** Séries encore récompensées (XP + HORA) aujourd'hui. */
export function rewardsLeftToday(state: AutomatismesState | null | undefined): number {
  const limit = state?.daily_reward_limit ?? AUTOMATISMES_DAILY_REWARDS;
  return Math.max(0, limit - (state?.rewarded_today ?? 0));
}

/**
 * Plus de série gratuite ce mois-ci et pas d'accès illimité. Une série EN COURS
 * se reprend toujours (jamais bloquée).
 */
export function automatismesLocked(state: AutomatismesState | null | undefined): boolean {
  const a = state?.access;
  if (!a || a.unlimited || state?.in_progress) return false;
  return (a.free_left ?? 0) <= 0;
}

/** « 1er novembre » à partir de « 2026-11-01 ». */
export function nextFreeLabel(date: string | null | undefined): string {
  if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return 'le mois prochain';
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const month = new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('fr-FR', {
    month: 'long',
    timeZone: 'UTC',
  });
  return `le ${d === 1 ? '1er' : d} ${month}`;
}

/** Ligne d'accès sous la carte (null si illimité). */
export function automatismesAccessLine(state: AutomatismesState | null | undefined): string | null {
  const a = state?.access;
  if (!a || a.unlimited) return null;
  if (automatismesLocked(state)) {
    return `Série gratuite du mois faite · prochaine ${nextFreeLabel(a.next_free_at)}`;
  }
  return '1 série gratuite par mois · illimité avec l’abonnement ou le programme';
}

/** Libellé du bouton principal. */
export function automatismesCta(state: AutomatismesState | null | undefined): string {
  const cur = state?.in_progress;
  if (cur) return `Reprendre (${cur.answered_count}/${cur.question_count})`;
  if (automatismesLocked(state)) return 'Débloquer les automatismes';
  return state?.sessions_count ? 'Nouvelle série' : 'Lancer ma 1re série';
}

/** « 5e · 4e · 3e » (libellés courts). */
export function automatismesLevelsLabel(levels: readonly string[] | undefined): string {
  const short: Record<string, string> = {
    '6e': '6e',
    '5e': '5e',
    '4e': '4e',
    '3e': '3e',
    '2nde': '2nde',
    '1re': '1re',
    tle: 'Tle',
    mpsi: 'MPSI',
    ptsi: 'PTSI',
    mp: 'MP',
    pt: 'PT',
  };
  return (levels ?? []).map((l) => short[l] ?? l).join(' · ');
}

export function automatismesErrorMessage(code: string): string {
  switch (code) {
    case 'NO_QUESTIONS_AVAILABLE':
      return 'Pas encore assez de questions pour ton niveau : les automatismes arrivent bientôt.';
    case 'NO_LEVEL':
      return 'Indique ton niveau dans ton profil pour faire les automatismes.';
    case 'NO_ANSWERS':
      return 'Réponds au moins à une question avant de terminer.';
    case 'LIMIT_REACHED':
      return 'Ta série gratuite du mois est faite. Débloque les automatismes en illimité, ou reviens le mois prochain.';
    default:
      return 'Une erreur est survenue. Réessaie.';
  }
}

export class AutomatismesError extends Error {
  constructor(public readonly code: string) {
    super(code);
    this.name = 'AutomatismesError';
  }
}

function unwrap<T>(data: unknown, error: { message: string } | null): T {
  if (error) throw new AutomatismesError('NETWORK_ERROR');
  if (data && typeof data === 'object' && 'error' in data) {
    throw new AutomatismesError(String((data as { error: string }).error));
  }
  return data as T;
}

// ── RPC + hooks ─────────────────────────────────────────────────────────────

export const automatismesStateKey = (userId: string) => ['automatismes-state', userId] as const;

export function useAutomatismesState(userId: string | null | undefined) {
  return useQuery({
    queryKey: automatismesStateKey(userId ?? ''),
    enabled: !!userId,
    staleTime: 30_000,
    queryFn: async (): Promise<AutomatismesState> => {
      const { data, error } = await db.rpc('get_automatismes_state');
      return unwrap<AutomatismesState>(data, error);
    },
  });
}

export async function startAutomatismes(restart = false): Promise<AutomatismesPlay> {
  const { data, error } = await db.rpc('start_automatismes_session', { p_restart: restart });
  return unwrap<AutomatismesPlay>(data, error);
}

export async function submitAutomatismeAnswer(input: {
  sessionId: string;
  questionId: string;
  choiceId: string;
  timeMs: number;
}): Promise<{ was_correct: boolean; explanation_md: string | null; feedback_md: string | null }> {
  const { data, error } = await db.rpc('submit_automatismes_answer', {
    p_session_id: input.sessionId,
    p_question_id: input.questionId,
    p_choice_id: input.choiceId,
    p_time_ms: input.timeMs,
  });
  return unwrap(data, error);
}

export async function finishAutomatismes(sessionId: string): Promise<AutomatismesResult> {
  const { data, error } = await db.rpc('finish_automatismes_session', {
    p_session_id: sessionId,
  });
  const result = unwrap<AutomatismesResult>(data, error);
  publishProgress('session-finished');
  return result;
}
